import React, { useState, useEffect } from 'react';
import { X, Mail, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EmailPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user has already seen/dismissed the popup
    const hasSeenPopup = localStorage.getItem('cvs_popup_dismissed');
    const hasSubscribed = localStorage.getItem('cvs_subscribed');
    
    if (!hasSeenPopup && !hasSubscribed) {
      // Show popup after 5 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('cvs_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/public/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        localStorage.setItem('cvs_subscribed', 'true');
        localStorage.setItem('cvs_subscriber_email', email);
        setSubmitted(true);
        
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      // Fallback to localStorage if API fails
      localStorage.setItem('cvs_subscribed', 'true');
      localStorage.setItem('cvs_subscriber_email', email);
      setSubmitted(true);
      
      setTimeout(() => {
        setIsOpen(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors z-10"
          aria-label="Close popup"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        {/* Header Image/Banner */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
            <Gift className="h-8 w-8 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Get 10% Off Your First Stay
          </h2>
          <p className="text-slate-300 text-sm">
            Subscribe and receive exclusive deals on premium DC rentals
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="pl-10 py-6 text-base"
                  data-testid="popup-email-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-6 text-base"
                disabled={loading}
                data-testid="popup-submit-btn"
              >
                {loading ? 'Subscribing...' : 'Claim My 10% Discount'}
              </Button>
              <p className="text-xs text-center text-slate-500">
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">You're In!</h3>
              <p className="text-slate-600 text-sm">
                Check your email for your exclusive 10% discount code.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={handleClose}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            No thanks, I'll pay full price
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;
